// src/components/Navbar.jsx
import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation, Link as RouterLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { setUser } from "../../actions/userActions";
import { logOut } from "../../actions/authActions";
import { setUserLanguage } from "../../utils/services/userService";
import config from "../../utils/config";
import { appCtx } from "../../utils/hooks/appContext";
import termoventLogo from "../../image/logo.png";

import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Tooltip from "@mui/material/Tooltip";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";

const Navbar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { setModalopen } = useContext(appCtx);
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const user = useSelector((state) => state.user);
  const { i18n, t } = useTranslation();

  const [anchorElUser, setAnchorElUser] = useState(null);
  const [anchorElMenu, setAnchorElMenu] = useState(null);
  const [language, setLanguage] = useState(i18n.language);

  // User menu items
  const userMenuItems = [
    { id: 1, label: t("navbarInternal.nameAccount"), to: "/account" },
    { id: 2, label: t("navbarInternal.nameDashboard"), to: "/home" },
    { id: 3, label: t("navbarInternal.nameLogout"), action: "logout" },
  ];

  // Language options
  const languages = [
    { value: "en", label: "EN", src: "https://flagcdn.com/w20/gb.png" },
    { value: "de", label: "DE", src: "https://flagcdn.com/w20/de.png" },
    { value: "sr", label: "SR", src: "https://flagcdn.com/w20/rs.png" },
    { value: "it", label: "IT", src: "https://flagcdn.com/w20/it.png" },
    { value: "ru", label: "RU", src: "https://flagcdn.com/w20/ru.png" },
  ];

  // Handlers
  const handleOpenUserMenu = (e) => setAnchorElUser(e.currentTarget);
  const handleCloseUserMenu = () => setAnchorElUser(null);
  const handleOpenMenu = (e) => setAnchorElMenu(e.currentTarget);
  const handleCloseMenu = () => setAnchorElMenu(null);
  const handleLogOut = () => {
    dispatch(logOut());
    handleCloseUserMenu();
    navigate("/");
  };
  const handleChangeLanguage = (e) => {
    const lng = e.target.value;
    i18n.changeLanguage(lng);
    setLanguage(lng);
    if (isLoggedIn) {
      setUserLanguage(localStorage.getItem(config.jwt_key_name), lng);
    }
  };

  // Restore user name on mount
  useEffect(() => {
    const name = localStorage.getItem("userName");
    dispatch(setUser(name));
  }, [dispatch]);

  // Sync language state
  useEffect(() => {
    setLanguage(i18n.language);
  }, [i18n.language]);

  return (
    <AppBar
      position="static"
      sx={{
        backgroundColor: "white",
        p: "15px 0",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        borderBottom: "1px solid #e0e0e0",
        maxHeight: "75px",
      }}
    >
      <Container maxWidth={false}>
        <Toolbar
          disableGutters
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            minHeight: "100% !important",
          }}
        >
          {/* Left: Logo */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Tooltip title="Back">
              <IconButton
                onClick={() => location.pathname !== "/" && navigate(-1)}
                sx={{ color: theme.palette.primary.dark }}
              >
                <ArrowBackIosIcon />
              </IconButton>
            </Tooltip>
            <Typography
              component="a"
              href="/"
              variant="h4"
              noWrap
              sx={{
                display: { xs: "none", md: "flex" },
                fontWeight: 700,
                letterSpacing: ".3rem",
                color: theme.palette.primary.dark,
                textDecoration: "none",
                alignItems: "center",
              }}
            >
              <img
                src={termoventLogo}
                alt="Termovent Logo"
                style={{ height: "30px" }}
              />
            </Typography>
          </Box>

          {/* Right: Menu & Language */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
            {/* Menu with Label */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.primary.dark,
                  fontWeight: 500,
                }}
              >
                Menu
              </Typography>
              <IconButton
                onClick={handleOpenMenu}
                sx={{
                  color: theme.palette.primary.dark,
                  p: 0.5,
                }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
            <Menu
              anchorEl={anchorElMenu}
              open={Boolean(anchorElMenu)}
              onClose={handleCloseMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              disableScrollLock
              PaperProps={{
                sx: {
                  backgroundColor: "#dbf5ff", // Lighter powder blue background
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  minWidth: "220px",
                  mt: 1,
                  overflow: "hidden",
                  "& .MuiMenuItem-root": {
                    color: "#2C2C2C",
                    fontSize: "15px",
                    fontWeight: 400,
                    fontFamily: "Montserrat, sans-serif",
                    py: 2,
                    px: 3,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    position: "relative",
                    "&:not(:last-child)::after": {
                      content: '""',
                      position: "absolute",
                      bottom: 0,
                      left: "24px", // Padding from left
                      right: "24px", // Padding from right
                      height: "1px",
                      backgroundColor: "#000000",
                    },
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.1)",
                    },
                    "& .MuiSvgIcon-root": {
                      fontSize: "18px",
                      color: "#2C2C2C",
                    },
                  },
                },
              }}
            >
              <MenuItem onClick={() => navigate("/account")}>
                {t("navbarInternal.nameAccount")}
                <ArrowForwardIcon />
              </MenuItem>
              <MenuItem onClick={() => navigate("/home")}>
                {t("navbarInternal.nameDashboard")}
                <ArrowForwardIcon />
              </MenuItem>
              <MenuItem onClick={handleLogOut}>
                {t("navbarInternal.nameLogout")}
                <ArrowForwardIcon />
              </MenuItem>
            </Menu>

            {/* Inline Language Buttons */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {languages.map((option) => (
                <Button
                  key={option.value}
                  onClick={() =>
                    handleChangeLanguage({ target: { value: option.value } })
                  }
                  sx={{
                    minWidth: "auto",
                    p: 1,
                    color:
                      language === option.value
                        ? theme.palette.primary.dark
                        : theme.palette.primary.main,
                    fontWeight: language === option.value ? 600 : 400,
                    fontSize: "14px",
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "transparent",
                      color: theme.palette.primary.dark,
                    },
                  }}
                >
                  {option.label}
                </Button>
              ))}
            </Box>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Navbar;
